import {appSessionStorage, appLocalStorage} from '../../../shared/services';

interface GlobalStateContract {
  formId: string,
    formKey: string,
    storeCode: string,
    authToken: string,
    isLoggedIn: boolean,
    customer: {
    email: string
  },
  customerEmail: string,
    quote: string,
    quoteId: string,
    quoteMask: string,
    quoteItems: string,
    lastAddedItem: object,
    quoteItemCount: string,
    shippingCountries: Array<{
    id: string,
    full_name_locale: string,
  }>,
}

const initialState: CallableFunction = (): GlobalStateContract => {
  return {
    formId: '',
    formKey: '',
    storeCode: null,
    authToken: appSessionStorage.getItem('auth_token', null),

    isLoggedIn: null !== appSessionStorage.getItem('checkout-customer', {}) ? (appSessionStorage.getItem('checkout-customer', {})).hasOwnProperty('entity_id') : false,
    customer: appSessionStorage.getItem('checkout-customer', false),
    customerEmail: appSessionStorage.getItem('checkout-customer-email', null),

    quote: appLocalStorage.getItem('checkout-quote', null),
    quoteId: appLocalStorage.getItem('checkout-quote-id', null),
    quoteMask: appLocalStorage.getItem('checkout-quote-mask', null),
    quoteItems: appSessionStorage.getItem('checkout-quote-items', null),
    lastAddedItem: {},
    quoteItemCount: appLocalStorage.getItem('checkout-quote-item-count', 0),

    shippingCountries: appSessionStorage.getItem('checkout-shipping-counties', []),
  };
};

const state: GlobalStateContract = initialState();

const getters = {
  isLoggedIn: state => state.isLoggedIn,
  getQuoteIdentifier: state => `${state.isLoggedIn ? state.quoteId : state.quoteMask}`,
  getCustomer: (state) => {
    if ((state.customer || {}).hasOwnProperty('email')) {
      state.customerEmail = state.customer.email;
    }
    return state.customer;
  },
  defaultShippingAddress: state => {
    return (state.customer || {}).hasOwnProperty('default_shipping_address')
      ? state.customer['default_shipping_address'] : null;
  },
  defaultBillingAddress: state => {
    return (state.customer || {}).hasOwnProperty('default_billing_address')
      ? state.customer['default_billing_address'] : null;
  },
  customerAddresses: state => {
    return (state.customer || {}).hasOwnProperty('addresses')
      ? state.customer['addresses'] : [];
  },
  // Api Url Section.
  getApiPath: state => {
    return `rest/${state.storeCode}/V1/${state.isLoggedIn ? 'carts/mine' : 'guest-carts'}`;
  },
  getApiPathWithQuoteId: state => {
    return `rest/${state.storeCode}/V1/${state.isLoggedIn ? 'carts/mine' : 'guest-carts/' + state.quoteId}`;
  },
  getApiPathWithQuoteMask: state => {
    return `rest/${state.storeCode}/V1/${state.isLoggedIn ? 'carts/mine' : 'guest-carts/' + state.quoteMask}`;
  },
  getBuckarooApiPathWithQuoteMask: state => {
    return `rest/${state.storeCode}/V1/${state.isLoggedIn ? 'buckaroo' : 'guest-buckaroo/' + state.quoteMask}`;
  },
  getShippingCountries: state => {
    if (state.shippingCountries.length > 0) {
      return state.shippingCountries;
    }

    return [{
      'id': 'NL',
      'full_name_locale': 'Nederland',
    }];
  }
};

const mutations: object = {
  SET_AUTH_TOKEN(state, authToken) {
    state.authToken = authToken;
  },
  SET_FORM_ID(state, formId) {
    state.formId = formId;
  },
  SET_FORM_KEY(state, formKey) {
    state.formKey = formKey;
  },
  SET_STORE_CODE(state, storeCode) {
    state.storeCode = storeCode;
  },
  SET_CUSTOMER(state, customer) {
    state.customer = customer;
    state.isLoggedIn = (customer || {}).hasOwnProperty('entity_id');
    if (state.customer.hasOwnProperty('entity_id')) {
      appSessionStorage.setItem('checkout-customer', customer || {});
    }
  },
  SET_CUSTOMER_EMAIL(state, customerEmail) {
    if ( ! (state.customer || {}).hasOwnProperty('entity_id')) {
      state.customer = {};
      state.customer.email = customerEmail;
      state.customerEmail = state.customer.email;
      appSessionStorage.setItem('checkout-customer-email', state.customerEmail || '');

      if ( ! state.customer.hasOwnProperty('entity_id')) {
        appSessionStorage.setItem('checkout-customer', state.customer || {});
      }
    }
  },
  SET_QUOTE(state, quote) {
    state.quote = quote;
    state.quoteId = quote['id'];
    state.quoteItems = quote['items'];
    state.quoteItemCount = quote['items_count'];

    appLocalStorage.setItem('checkout-quote', quote);
    appLocalStorage.setItem('checkout-quote-id', quote['id']);
    appSessionStorage.setItem('checkout-quote-items', quote['items']);
    appLocalStorage.setItem('checkout-quote-item-count', quote['items_count']);
  },
  SET_QUOTE_MASK(state, quoteMask) {
    state.quoteMask = quoteMask;
    appLocalStorage.setItem('checkout-quote-mask', quoteMask);
  },
  SET_QUOTE_ITEMS(state, quoteItems) {
    state.quoteItems = quoteItems;
    appSessionStorage.setItem('checkout-quote-items', quoteItems);
  },
  SET_LAST_ADDED_ITEM(state, quoteItem) {
    state.lastAddedItem = JSON.parse(quoteItem);
  },
  SET_QUOTE_ITEM_COUNT(state, count) {
    state.quoteItemCount = count;
    appLocalStorage.setItem('checkout-quote-item-count', count);
  },
  SET_SHIPPING_COUNTRIES(state, payload) {
    state.shippingCountries = payload;
    appSessionStorage.setItem('checkout-shipping-counties', payload);
  }
};

const actions: object = {
  async initialize({state, commit, dispatch}) {
    if (null === state.storeCode) {
      commit('SET_STORE_CODE', window['Config']['active_store_code'] || 'default');
    }

    let _urlHash: string = null;
    if (window.location.hash.length && (window.location.href.indexOf("checkout") > -1)) {
      _urlHash = window.location.hash;
      history.replaceState("", document.title, window.location.href.replace(window.location.hash, ''));
    }

    if (_urlHash === '#clear') {
      let arr: Array<string> = []; // Array to hold the keys
      // Iterate over localStorage and insert the keys that meet the condition into arr
      for (let i = 0; i < localStorage.length; i++) {
        if (localStorage.key(i).startsWith('checkout-')) {
          arr.push(localStorage.key(i));
        }
      }

      // Iterate over arr and remove the items by key
      for (let i = 0; i < arr.length; i++) {
        localStorage.removeItem(arr[i]);
      }

      arr = []; // Array to hold the keys
      // Iterate over localStorage and insert the keys that meet the condition into arr
      for (let i = 0; i < sessionStorage.length; i++) {
        if (sessionStorage.key(i).startsWith('checkout-')) {
          arr.push(sessionStorage.key(i));
        }
      }

      // Iterate over arr and remove the items by key
      for (let i = 0; i < arr.length; i++) {
        sessionStorage.removeItem(arr[i]);
      }

      await dispatch('CheckoutCart/fetchQuote', null, {root: true});
    }

    if (_urlHash === '#login') {
      appSessionStorage.removeItem('checkout-billing-address');
      appSessionStorage.removeItem('checkout-shipping-address');
      commit('CheckoutCart/SET_BILLING_ADDRESS', {});
      commit('CheckoutCart/SET_SHIPPING_ADDRESS', {});
    }

    if (state.authToken !== null && ! state.customer.hasOwnProperty('entity_id')) {
      try {
        const {data} = await this.$solarClient.get('/api/customer/getCurrent');
        if (data) {

          await commit('SET_CUSTOMER', data);
          await dispatch('CheckoutCart/mergeQuote', null, {root: true});
        }
      } catch (e) {
        //
      }
    }

    if (null === state.quoteId && state.quoteMask) {
      await dispatch('CheckoutCart/fetchQuote', null, {root: true});
    }
  },
  async reInitializeCustomer({commit, state}) {
    if (state.authToken !== null) {
      try {
        const {data} = await this.$solarClient.get('/api/customer/getCurrent');
        if (data) {
          await commit('SET_CUSTOMER', data);
        }
      } catch (e) {
        //
      }
    }
  },
  async getActiveQuote({commit, dispatch}) {
    if (null === state.quoteMask || ( ! state.isLoggedIn && state.quoteId === state.quoteMask)) {
      await dispatch('CheckoutCart/createQuote', null, {root: true});
    }

    if (null === state.quoteId) {
      await dispatch('CheckoutCart/fetchQuote', null, {root: true});
    }
  },
  async createNewQuote({commit, dispatch}) {
    await dispatch('CheckoutCart/createQuote', null, {root: true});
  },
  setFormId({commit}, formId) {
    commit('SET_FORM_ID', formId);
  },
  setFormKey({commit}, formKey) {
    commit('SET_FORM_KEY', formKey);
  },
  async collectShippingCountries({commit, rootState}) {
    try {
      const {data} = await this.$mageClient.get(`/rest/${rootState['CheckoutGlobal']['storeCode']}/V1/directory/countries`);
      if (data) {
        commit('SET_SHIPPING_COUNTRIES', data);
      }
    } catch (e) {
      //
    }
  }
};

const CheckoutGlobal: object = {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};

export default CheckoutGlobal;
